import type { Booking, BookingStage } from '../api/bookings';

/**
 * Where a booking is, as the tracker on its detail screen draws it — on the website and the phone.
 *
 * <p>
 * <strong>Six steps, not ten — and the four that are missing are the point.</strong> Ready,
 * Issued, Returned and Inspected are facts about individual garments, not about the booking, so they
 * are drawn on each garment instead. A booking whose sherwani is ready and whose tuxedo is at the
 * tailor has no single answer to "is it ready", and a tracker that demanded one would have to pick
 * a garment and lie about the rest.
 * </p>
 *
 * <p>
 * An exit — cancelled, expired, or gone to recovery — is not a seventh step. It stops the track
 * where it stopped and says so beside it: "this booking ended at Confirmed" is the useful sentence,
 * and a greyed run of future stages would imply they are still coming.
 * </p>
 */

/** The six stages a booking passes through in order; the other three are exits. */
export const bookingStageSequence: readonly BookingStage[] = [
  'Enquiry',
  'Quotation',
  'Hold',
  'Confirmed',
  'InProgress',
  'Completed',
];

/** When each stage was reached. Absent, null and undefined all mean "not yet". */
export type StageDates = Partial<Record<BookingStage, string | null | undefined>>;

/** The dates the booking itself can answer for. */
export function stageDates(booking: Booking): StageDates {
  return {
    Quotation: booking.quotationNumber ? booking.chargeFromDate : null,
    Hold: booking.holdExpiresOn,
    InProgress: booking.wearers
      .flatMap((wearer) => wearer.garments)
      .find((garment) => garment.issuedOn)?.issuedOn,
  };
}

export type StepState = 'done' | 'current' | 'upcoming';

export interface StageStep {
  stage: BookingStage;
  on: string | null;
  state: StepState;
  /** After an exit's stop: drawn as never reached, not as still to come. */
  muted: boolean;
}

export interface StageTrack {
  steps: StageStep[];
  /** True when the booking left the track (cancelled, expired, recovery). */
  exited: boolean;
}

export function stageTrack(stage: BookingStage, dates: StageDates = {}): StageTrack {
  const reached = bookingStageSequence.indexOf(stage);
  const exited = reached === -1;

  // An exit keeps whatever it had reached: the last stage with a date on it is where it stopped.
  const stopped = exited
    ? bookingStageSequence.reduce((last, candidate, index) => (dates[candidate] ? index : last), -1)
    : reached;

  return {
    exited,
    steps: bookingStageSequence.map((candidate, index) => ({
      stage: candidate,
      on: dates[candidate] ?? null,
      state: index < stopped ? 'done' : index === stopped ? 'current' : 'upcoming',
      muted: exited && index > stopped,
    })),
  };
}
